import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@store/types';
import { AirflowDashboardHandler } from '@api/airflow-dashboard-handler';
import { errorAlert, successAlert } from '@components/toastify/notify-toast';
import { itemsToText, LogEntry, LogItem, parseLogContent } from '../../log-parser';
import { AirflowTaskInstance } from '../../types';

export const FILTER_ALL = '__all__';

export type VisibleLogRow =
    | { kind: 'group-header'; groupId: string; name: string; indent: number; isOpen: boolean }
    | { kind: 'log-line'; entry: LogEntry; indent: number; lineNumber: number };

/*
 * Manages the Logs tab: try-number navigation, fetching + parsing log content, level/source
 * filters (keeping group ancestors of surviving lines), group collapse state, and copy/download
 */
export function useLogsTab(dagId: string, runId: string, taskId: string, taskInstance: AirflowTaskInstance | null, mapIndex: number) {
    const { envVariables: Env } = useSelector((store: RootState) => store.AccountReducer);
    const [selectedTry, setSelectedTry] = useState(1);
    const [items, setItems] = useState<LogItem[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [levelFilter, setLevelFilter] = useState(FILTER_ALL);
    const [sourceFilter, setSourceFilter] = useState(FILTER_ALL);
    const [openGroups, setOpenGroups] = useState<Set<string>>(new Set());
    const hasManuallyChangedTry = useRef(false);

    useEffect(() => {
        if (taskInstance && !hasManuallyChangedTry.current) {
            setSelectedTry(taskInstance.try_number ?? 1);
        }
    }, [taskInstance]);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);
        AirflowDashboardHandler.getTaskLogs(Env, dagId, runId, taskId, selectedTry, mapIndex)
            .then((response) => {
                if (cancelled) return;
                const parsed = parseLogContent(response.content);
                setItems(parsed);
                setOpenGroups(new Set(parsed.filter((item): item is Extract<LogItem, { kind: 'group-start' }> => item.kind === 'group-start').map((item) => item.groupId)));
            })
            .catch(() => {
                if (!cancelled) errorAlert('Failed to load task logs.');
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [Env, dagId, runId, taskId, selectedTry, mapIndex]);

    const levelOptions = useMemo(
        () => Array.from(new Set(items.filter((item): item is Extract<LogItem, { kind: 'line' }> => item.kind === 'line').map((item) => item.entry.level).filter(Boolean))) as string[],
        [items]
    );
    const sourceOptions = useMemo(
        () => Array.from(new Set(items.filter((item): item is Extract<LogItem, { kind: 'line' }> => item.kind === 'line').map((item) => item.entry.source).filter(Boolean))) as string[],
        [items]
    );

    /*
     * Filters lines by level/source, keeping every ancestor group of a surviving line so the
     * group nesting stays intact around matched content
     */
    const filteredItems = useMemo(() => {
        const parentOf = new Map<string, string | null>();
        const stack: string[] = [];
        items.forEach((item) => {
            if (item.kind === 'group-start') {
                parentOf.set(item.groupId, stack[stack.length - 1] ?? null);
                stack.push(item.groupId);
            } else if (item.kind === 'group-end') {
                stack.pop();
            }
        });

        const matches = (entry: Extract<LogItem, { kind: 'line' }>['entry']) =>
            (levelFilter === FILTER_ALL || entry.level === levelFilter) && (sourceFilter === FILTER_ALL || entry.source === sourceFilter);

        const keepGroupIds = new Set<string>();
        items.forEach((item) => {
            if (item.kind === 'line' && matches(item.entry) && item.groupId) {
                let current: string | null = item.groupId;
                while (current) {
                    keepGroupIds.add(current);
                    current = parentOf.get(current) ?? null;
                }
            }
        });

        return items.filter((item) => {
            if (item.kind === 'line') return matches(item.entry);
            return keepGroupIds.has(item.groupId);
        });
    }, [items, levelFilter, sourceFilter]);

    /*
     * Flattens filtered items into visible rows, hiding the contents of any collapsed group
     */
    const visibleItems = useMemo(() => {
        const rows: VisibleLogRow[] = [];
        const groupStack: string[] = [];
        let skipUntilGroupId: string | null = null;
        let lineNumber = 0;

        filteredItems.forEach((item) => {
            if (item.kind === 'group-start') {
                if (skipUntilGroupId !== null) {
                    groupStack.push(item.groupId);
                    return;
                }
                groupStack.push(item.groupId);
                rows.push({ kind: 'group-header', groupId: item.groupId, name: item.name, indent: groupStack.length - 1, isOpen: openGroups.has(item.groupId) });
                if (!openGroups.has(item.groupId)) skipUntilGroupId = item.groupId;
                return;
            }
            if (item.kind === 'group-end') {
                const closedGroupId = groupStack.pop();
                if (skipUntilGroupId === closedGroupId) skipUntilGroupId = null;
                return;
            }
            if (skipUntilGroupId !== null) return;
            lineNumber += 1;
            rows.push({ kind: 'log-line', entry: item.entry, indent: groupStack.length, lineNumber });
        });

        return rows;
    }, [filteredItems, openGroups]);

    /*
     * Toggles a group's collapsed state
     */
    const handleToggleGroup = useCallback((groupId: string) => {
        setOpenGroups((prev) => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    }, []);

    /*
     * Navigates to the previous try number
     */
    const handlePrevTry = useCallback(() => {
        hasManuallyChangedTry.current = true;
        setSelectedTry((prev) => Math.max(1, prev - 1));
    }, []);

    /*
     * Navigates to the next try number, capped at the task instance's latest try
     */
    const handleNextTry = useCallback(() => {
        hasManuallyChangedTry.current = true;
        setSelectedTry((prev) => Math.min(taskInstance?.try_number ?? prev, prev + 1));
    }, [taskInstance]);

    /*
     * Copies the currently visible log lines as plain text
     */
    const handleCopy = useCallback(() => {
        navigator.clipboard.writeText(itemsToText(filteredItems));
        successAlert('Copied logs to clipboard.');
    }, [filteredItems]);

    /*
     * Downloads the currently visible log lines as a .log file
     */
    const handleDownload = useCallback(() => {
        const blob = new Blob([itemsToText(filteredItems)], { type: 'text/plain' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${dagId}__${taskId}__try${selectedTry}.log`;
        link.click();
        URL.revokeObjectURL(url);
    }, [filteredItems, dagId, taskId, selectedTry]);

    return {
        selectedTry,
        maxTry: taskInstance?.try_number ?? 1,
        isLoading,
        levelFilter,
        setLevelFilter,
        sourceFilter,
        setSourceFilter,
        levelOptions,
        sourceOptions,
        visibleItems,
        handleToggleGroup,
        handlePrevTry,
        handleNextTry,
        handleCopy,
        handleDownload,
    };
}
